import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "dumpd_admin_token";

const inputCls =
  "w-full bg-black border border-zinc-800 rounded-xl px-4 py-3 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-white transition-colors";
const labelCls = "block text-xs font-mono uppercase tracking-wider text-zinc-300 mb-2";

const authHeaders = () => {
  const t = localStorage.getItem(TOKEN_KEY);
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const loginWithGoogle = () => {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/admin";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    const sessionId = location.hash.split("session_id=")[1]?.split("&")[0];
    if (!sessionId) {
      navigate("/admin", { replace: true });
      return;
    }
    axios
      .post(`${API}/auth/google/session`, { session_id: sessionId }, { withCredentials: true })
      .then((res) => navigate("/admin", { replace: true, state: { user: res.data } }))
      .catch((err) => {
        const detail = err.response?.data?.detail;
        toast.error(typeof detail === "string" ? detail : "Google sign-in failed.");
        window.history.replaceState(null, "", "/admin");
        navigate("/admin", { replace: true });
      });
  }, []);

  return (
    <div className="min-h-screen bg-black flex items-center justify-center text-zinc-500 text-xs font-mono" data-testid="auth-callback">
      Signing you in…
    </div>
  );
}

function Login({ onLogin }) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { password });
      localStorage.setItem(TOKEN_KEY, data.token);
      onLogin();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Login failed. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center px-4" data-testid="admin-login">
      <form onSubmit={submit} className="w-full max-w-sm bg-zinc-950 border border-zinc-800 rounded-2xl p-8 space-y-5">
        <div>
          <h1 className="text-2xl font-black uppercase tracking-tight font-display text-white">DUMP'D Dispatch</h1>
          <p className="text-xs font-mono text-zinc-400 mt-1">Owner access only</p>
        </div>
        <div>
          <label className={labelCls} htmlFor="admin-password">Password</label>
          <input
            id="admin-password"
            data-testid="admin-password-input"
            type="password"
            className={inputCls}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter owner password"
            required
          />
        </div>
        {error && <p data-testid="admin-login-error" className="text-xs text-red-400 font-mono">{error}</p>}
        <button
          type="submit"
          data-testid="admin-login-btn"
          disabled={busy}
          className="w-full py-3.5 rounded-xl bg-white text-black font-black uppercase text-sm tracking-wider hover:bg-zinc-200 transition-colors disabled:opacity-60"
        >
          {busy ? "Checking..." : "Sign In"}
        </button>
        <div className="flex items-center gap-3 text-xs font-mono text-zinc-500">
          <span className="flex-1 border-t border-zinc-800" />
          or
          <span className="flex-1 border-t border-zinc-800" />
        </div>
        <button
          type="button"
          data-testid="admin-google-login-btn"
          onClick={loginWithGoogle}
          className="w-full py-3.5 rounded-xl bg-zinc-900 border border-zinc-700 text-white font-bold uppercase text-xs tracking-wider hover:bg-zinc-800 transition-colors"
        >
          Sign in with Google
        </button>
        <a href="/" className="block text-center text-xs font-mono text-zinc-400 hover:text-white transition-colors">← Back to site</a>
      </form>
    </div>
  );
}

function ReviewManager({ token }) {
  const [reviews, setReviews] = useState([]);
  const [form, setForm] = useState({ quote: "", author: "", location: "", tag: "" });

  const load = () => axios.get(`${API}/reviews`).then((r) => setReviews(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/reviews`, form, { headers: authHeaders(), withCredentials: true });
      toast.success("Review published to the site.");
      setForm({ quote: "", author: "", location: "", tag: "" });
      load();
    } catch {
      toast.error("Could not add review.");
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/admin/reviews/${id}`, { headers: authHeaders(), withCredentials: true });
      toast.success("Review removed.");
      load();
    } catch {
      toast.error("Could not remove review.");
    }
  };

  return (
    <div className="bg-zinc-950 border border-zinc-800 rounded-2xl p-6" data-testid="review-manager">
      <h2 className="text-lg font-black uppercase text-white font-display mb-1">Customer Reviews</h2>
      <p className="text-xs text-zinc-400 mb-5">These appear in the Client Experiences section. Delete the samples once real ones are in.</p>

      <div className="space-y-3 mb-6">
        {reviews.map((r) => (
          <div key={r.id} data-testid={`admin-review-${r.id}`} className="flex items-start justify-between gap-4 p-4 rounded-xl bg-black border border-zinc-800">
            <div className="text-xs text-zinc-300">
              <p className="italic leading-relaxed">{r.quote}</p>
              <p className="font-mono text-zinc-500 mt-2">
                {r.author} · {r.location} {r.is_sample && <span className="text-zinc-300 border border-zinc-700 rounded px-1.5 py-0.5 ml-1">SAMPLE</span>}
              </p>
            </div>
            <button
              data-testid={`review-delete-${r.id}`}
              onClick={() => remove(r.id)}
              className="shrink-0 text-xs font-mono uppercase text-zinc-400 hover:text-white border border-zinc-700 hover:border-white/50 rounded-lg px-3 py-1.5 transition-colors"
            >
              Delete
            </button>
          </div>
        ))}
        {reviews.length === 0 && <p className="text-xs font-mono text-zinc-500">No reviews yet.</p>}
      </div>

      <form onSubmit={add} className="space-y-3 border-t border-zinc-800 pt-5" data-testid="review-add-form">
        <textarea data-testid="review-quote-input" rows="2" placeholder="Customer quote…" className={inputCls} value={form.quote} onChange={(e) => setForm({ ...form, quote: e.target.value })} required />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <input data-testid="review-author-input" placeholder="Name / label" className={inputCls} value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} required />
          <input data-testid="review-location-input" placeholder="City, FL" className={inputCls} value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} required />
          <input data-testid="review-tag-input" placeholder="Job type (optional)" className={inputCls} value={form.tag} onChange={(e) => setForm({ ...form, tag: e.target.value })} />
        </div>
        <button type="submit" data-testid="review-add-btn" className="px-5 py-2.5 rounded-xl bg-white text-black font-bold uppercase text-xs tracking-wider hover:bg-zinc-200 transition-colors">
          Publish Review
        </button>
      </form>
    </div>
  );
}

function Dashboard({ onLogout }) {
  const [leads, setLeads] = useState(null);
  const token = localStorage.getItem(TOKEN_KEY);

  const load = () =>
    axios.get(`${API}/leads`, { headers: authHeaders(), withCredentials: true }).then((r) => setLeads(r.data)).catch(() => setLeads([]));
  useEffect(() => { load(); }, []);

  return (
    <div className="min-h-screen bg-black text-white px-4 sm:px-6 lg:px-8 py-10" data-testid="admin-dashboard">
      <div className="max-w-6xl mx-auto space-y-10">
        <div className="flex items-center justify-between gap-4 flex-wrap border-b border-white/10 pb-6">
          <div>
            <h1 className="text-3xl font-black uppercase tracking-tight font-display">Dispatch Dashboard</h1>
            <p className="text-xs font-mono text-zinc-400 mt-1">DUMP'D Junk Removal · Owner View</p>
          </div>
          <div className="flex gap-3">
            <a href="/" data-testid="admin-back-link" className="px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wider bg-zinc-900 border border-zinc-700 hover:bg-zinc-800 transition-colors">View Site</a>
            <button onClick={onLogout} data-testid="admin-logout-btn" className="px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wider bg-zinc-900 border border-zinc-700 hover:bg-zinc-800 transition-colors">Log Out</button>
          </div>
        </div>

        <div className="bg-zinc-950 border border-zinc-800 rounded-2xl p-6" data-testid="leads-panel">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-lg font-black uppercase text-white font-display">Quote Requests</h2>
            <button onClick={load} data-testid="leads-refresh-btn" className="text-xs font-mono uppercase text-zinc-400 hover:text-white border border-zinc-700 rounded-lg px-3 py-1.5 transition-colors">Refresh</button>
          </div>
          {leads === null && <p className="text-xs font-mono text-zinc-500">Loading…</p>}
          {leads !== null && leads.length === 0 && <p className="text-xs font-mono text-zinc-500">No quote requests yet.</p>}
          <div className="space-y-3">
            {(leads || []).map((l) => (
              <div key={l.id} data-testid={`lead-row-${l.id}`} className="p-4 rounded-xl bg-black border border-zinc-800 text-sm">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="font-bold text-white">{l.name} <span className="text-zinc-500 font-mono text-xs">· {l.city}</span></div>
                  <a href={`tel:${l.phone}`} className="font-mono text-xs text-white underline underline-offset-4">{l.phone}</a>
                </div>
                <div className="text-xs font-mono text-zinc-400 mt-1">{l.service}</div>
                {l.details && <p className="text-xs text-zinc-400 mt-2 leading-relaxed">{l.details}</p>}
                {l.photos && l.photos.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {l.photos.map((p) => (
                      <a key={p} href={`${API}/files/${p}?auth=${token}`} target="_blank" rel="noopener noreferrer" data-testid={`lead-photo-${p}`}>
                        <img src={`${API}/files/${p}?auth=${token}`} alt="customer junk" className="w-16 h-16 rounded-lg object-cover border border-zinc-700 hover:border-white/50 transition-colors" />
                      </a>
                    ))}
                  </div>
                )}
                <div className="text-[10px] font-mono text-zinc-600 mt-3">{new Date(l.created_at).toLocaleString()}</div>
              </div>
            ))}
          </div>
        </div>

        <ReviewManager token={token} />
      </div>
    </div>
  );
}

function AdminInner() {
  const location = useLocation();
  const [authed, setAuthed] = useState(location.state?.user ? true : null);

  useEffect(() => {
    // CRITICAL: if returning from the OAuth callback, skip this check —
    // AuthCallback exchanges the session_id and establishes the session first.
    if (location.hash?.includes("session_id=")) return;
    if (location.state?.user) return;
    axios
      .get(`${API}/auth/me`, { headers: authHeaders(), withCredentials: true })
      .then(() => setAuthed(true))
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        setAuthed(false);
      });
  }, []);

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {}
    localStorage.removeItem(TOKEN_KEY);
    setAuthed(false);
  };

  if (authed === null) {
    return <div className="min-h-screen bg-black flex items-center justify-center text-zinc-500 text-xs font-mono">Loading…</div>;
  }
  return authed ? <Dashboard onLogout={logout} /> : <Login onLogin={() => setAuthed(true)} />;
}

export default function Admin() {
  const location = useLocation();
  // Detect session_id synchronously during render (not useEffect) to avoid race conditions
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return <AdminInner />;
}
